import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import SpeedIcon from '@mui/icons-material/Speed';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ReportIcon from '@mui/icons-material/Report';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WeatherWidget from '../WeatherWidget';

const drawerWidth = 240;

// 根據用戶角色獲取側邊欄菜單項
const getMenuItems = (role) => {
  const baseItems = [
    { text: '首頁', icon: <HomeIcon />, path: '/' }
  ];

  switch (role) {
    case 'resident':
      return [
        ...baseItems,
        { text: 'CCTV 監控', icon: <VideoCallIcon />, path: '/resident/cctv' },
        { text: '公用設施讀數', icon: <SpeedIcon />, path: '/resident/meters' },
        { text: '通知', icon: <NotificationsIcon />, path: '/resident/notifications' },
        { text: '付款上傳', icon: <PaymentIcon />, path: '/resident/payments' },
        { text: '訪客通行證', icon: <VpnKeyIcon />, path: '/resident/visitor-access' }
      ];
    case 'tenant':
      return [
        ...baseItems,
        { text: 'CCTV 監控', icon: <VideoCallIcon />, path: '/tenant/cctv' },
        { text: '公用設施讀數', icon: <SpeedIcon />, path: '/tenant/meters' },
        { text: '通知', icon: <NotificationsIcon />, path: '/tenant/notifications' },
        { text: '付款上傳', icon: <PaymentIcon />, path: '/tenant/payments' },
        { text: '訪客通行證', icon: <VpnKeyIcon />, path: '/tenant/visitor-access' }
      ];
    case 'security':
      return [
        ...baseItems,
        { text: 'CCTV 監控', icon: <VideoCallIcon />, path: '/security/cctv' },
        { text: '安全警報', icon: <NotificationsIcon />, path: '/security/alerts' },
        { text: '通告查看', icon: <NotificationsIcon />, path: '/security/announcements' },
        { text: 'NFC 巡邏', icon: <AssignmentIcon />, path: '/security/patrol' },
        { text: '問題報告', icon: <ReportIcon />, path: '/security/issues' },
        { text: '工作訂單', icon: <AssignmentIcon />, path: '/security/work-orders' }
      ];
    case 'manager':
      return [
        ...baseItems,
        { text: 'CCTV 監控', icon: <VideoCallIcon />, path: '/manager/cctv' },
        { text: '公用設施讀數', icon: <SpeedIcon />, path: '/manager/meters' },
        { text: '安全警報', icon: <NotificationsIcon />, path: '/manager/alerts' },
        { text: '通告管理', icon: <NotificationsIcon />, path: '/manager/announcements' },
        { text: 'NFC 巡邏', icon: <AssignmentIcon />, path: '/manager/patrol' },
        { text: '問題報告', icon: <ReportIcon />, path: '/manager/issues' },
        { text: '工作訂單', icon: <AssignmentIcon />, path: '/manager/work-orders' }
      ];
    case 'visitor':
      return [
        ...baseItems,
        { text: '訪客通行', icon: <VpnKeyIcon />, path: '/visitor/access' }
      ];
    default:
      return baseItems;
  }
};

const MainLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const { userRole, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const menuItems = getMenuItems(userRole);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    // 處理登出邏輯
    handleProfileMenuClose();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          智能大廈系統
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => handleNavigate(item.path)}>
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {userRole === 'resident' && '住戶儀表板'}
            {userRole === 'tenant' && '租戶儀表板'}
            {userRole === 'security' && '保安員儀表板'}
            {userRole === 'manager' && '物業管理員儀表板'}
            {userRole === 'visitor' && '訪客通行'}
          </Typography>
          <WeatherWidget />
          <IconButton
            color="inherit"
            edge="end"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate('/profile');
            }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>個人資料</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToAppIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>登出</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // 更好的移動裝置性能
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout; 